import i18n from "@/i18n";
import { transitionToStale } from "@/features/engagement/engagement-state";
import type {
  ComputeLogEntry,
  EngagementAccessMeta,
  EngagementHistoryEntry,
  EngagementMember,
  EngagementWithAccess,
  HistoryEntry,
  HistoryStore,
  PendingTierRequest,
  TierChangeRequest,
  UsageStats,
  UserProfile,
} from "@/types";

import type { HistoryStorage } from "../interfaces";

const STORAGE_KEY = "ifrs16:history";
const ENGAGEMENT_KEY = "ifrs16:engagements";
const MAX_ENTRIES = 50;
const SCHEMA_VERSION = 1;
/**
 * Phase 6a.18a — bumped 2 → 3. The shape on disk is unchanged (the
 * `lastResult` and `isStale` fields were already part of the v2 entry
 * type), but the version bump runs the v2 → v3 migration that defensively
 * fills `isStale = false` for any entry that might have been written
 * before the field became required.
 */
const ENGAGEMENT_SCHEMA_VERSION = 3;
const PURGE_AFTER_DAYS = 7;

interface EngagementStore {
  version: 3;
  entries: EngagementHistoryEntry[];
  /** True after the v1→v2 seed migration has run, so it never re-seeds. */
  v1Migrated: boolean;
}

/**
 * Phase 6a.18a — pluggable QuotaExceededError sink. The default is a
 * destructive toast via sonner; tests inject a mock so they don't need
 * a Toaster mounted in the React tree.
 */
let quotaExceededHandler: (message: string) => void = (message) => {
  // Lazy-load `sonner` so unit tests can swap this before any module
  // import side-effects fire.
  void import("sonner").then(({ toast }) => {
    toast.error(message);
  });
};

export function setQuotaExceededHandler(
  handler: (message: string) => void,
): void {
  quotaExceededHandler = handler;
}

/**
 * localStorage-backed history — Phase 6a.5 (single-contract) and
 * Phase 6a.8b (engagement) per Frontend Spec §8.2 / §16 / §21.11.
 *
 * Two independent stores share this class behind the `HistoryStorage`
 * interface:
 *
 *   1. `ifrs16:history` (v1) — single-contract entries, unchanged from
 *      Phase 6a.5. The legacy `/new` → results flow continues to use this.
 *
 *   2. `ifrs16:engagements` (v2) — engagement entries (Phase 6a.8b).
 *      On first read, runs the v1→v2 seed migration silently per Ömer's
 *      directive: each existing single-contract entry becomes a migrated
 *      "(Migrated Calculation)" engagement with `isStale: true` so the
 *      user is prompted to recalculate. A `console.log` summary is the
 *      only user-visible feedback (no banner).
 *
 * Both stores observe FIFO eviction at MAX_ENTRIES (50). Engagement
 * entries are soft-deleted (`isDeleted: true`); `purgeOldDeleted()`
 * hard-deletes anything past `PURGE_AFTER_DAYS`.
 *
 * Phase 12 swaps this for a SupabaseHistoryStorage behind the same
 * HistoryStorage interface — no component changes needed.
 */
export class LocalStorageHistoryStorage implements HistoryStorage {
  // ── Single-contract (legacy v1) ──────────────────────────────────────────

  async list(): Promise<HistoryEntry[]> {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    try {
      const store = JSON.parse(raw) as HistoryStore;
      if (store.version !== SCHEMA_VERSION) {
        // Future-proof: handle version migrations here.
        console.warn("Unknown history schema version, resetting");
        return [];
      }
      return store.entries ?? [];
    } catch {
      return [];
    }
  }

  async add(entry: HistoryEntry): Promise<void> {
    const entries = await this.list();
    entries.unshift(entry); // newest first
    if (entries.length > MAX_ENTRIES) entries.pop(); // FIFO eviction
    this.save(entries);
  }

  async delete(id: string): Promise<void> {
    const entries = (await this.list()).filter((e) => e.id !== id);
    this.save(entries);
  }

  async deleteMany(ids: string[]): Promise<void> {
    const idSet = new Set(ids);
    const entries = (await this.list()).filter((e) => !idSet.has(e.id));
    this.save(entries);
  }

  async clear(): Promise<void> {
    localStorage.removeItem(STORAGE_KEY);
  }

  async exportJson(): Promise<Blob> {
    const entries = await this.list();
    const engagements = await this.listEngagements();
    const json = JSON.stringify(
      {
        version: SCHEMA_VERSION,
        exportedAt: new Date().toISOString(),
        entries,
        engagements,
      },
      null,
      2,
    );
    return new Blob([json], { type: "application/json" });
  }

  private save(entries: HistoryEntry[]): void {
    const store: HistoryStore = { version: SCHEMA_VERSION, entries };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch (err) {
      if (isQuotaExceeded(err)) {
        // Phase 6a.18a — silent failure + toast, matching engagement
        // store behaviour. Legacy single-contract path is on its way
        // out but should not surprise-throw mid-save either.
        const message = i18n.t("engagement.storage.quotaExceeded");
        quotaExceededHandler(message);
        return;
      }
      throw err;
    }
  }

  // ── Engagement CRUD (Phase 6a.8b) ────────────────────────────────────────

  async listEngagements(): Promise<EngagementWithAccess[]> {
    const store = this.readEngagementStore();
    // Local mode has no sharing — every engagement is the user's own.
    return store.entries
      .filter((e) => !e.isDeleted)
      .map((e) => ({
        ...e,
        _access: {
          isOwner: true,
          myRole: null,
          myCanInvite: false,
          ownerEmail: null,
          isAdmin: false,
        },
      }));
  }

  async getEngagement(id: string): Promise<EngagementHistoryEntry | null> {
    const store = this.readEngagementStore();
    return store.entries.find((e) => e.id === id && !e.isDeleted) ?? null;
  }

  async saveEngagement(entry: EngagementHistoryEntry): Promise<void> {
    const store = this.readEngagementStore();
    const idx = store.entries.findIndex((e) => e.id === entry.id);
    if (idx >= 0) {
      store.entries[idx] = entry;
    } else {
      store.entries.unshift(entry);
      if (store.entries.length > MAX_ENTRIES) store.entries.pop();
    }
    this.writeEngagementStore(store);
  }

  /**
   * Soft-delete with payload reclaim (BUG-1a).
   *
   * The entry stays in the store (so the 7-day undo window and the
   * `purgeOldDeleted` TTL still work), but its heavy `lastResult`
   * snapshot is dropped immediately. A deleted 16-contract engagement
   * shrinks from ~2.8 MB to a few KB, so accumulated soft-deletes can
   * no longer silently exhaust the localStorage quota and block
   * `Hesapla`.
   *
   * Reclaim resets the calculation lifecycle to draft-state metadata
   * (`lastResult`/`calculatedAt`/`staleSince`/`lastCalculateError`
   * cleared, `status: "draft"`, `isStale: false`) so that if the user
   * undoes the delete, the restored engagement is a clean draft they
   * recalculate — there is no stale snapshot to mislead any consumer.
   * The undo path in HistoryPage relies on this shape.
   */
  async deleteEngagement(id: string): Promise<void> {
    const store = this.readEngagementStore();
    const target = store.entries.find((e) => e.id === id);
    if (!target) return;
    const now = new Date().toISOString();
    target.isDeleted = true;
    target.deletedAt = now;
    target.updatedAt = now;
    // Reclaim the heavy result payload + reset lifecycle to draft.
    target.lastResult = undefined;
    target.status = "draft";
    target.isStale = false;
    target.calculatedAt = undefined;
    target.staleSince = undefined;
    target.lastCalculateError = undefined;
    this.writeEngagementStore(store);
  }

  // ── Per-contract hard delete (Phase 6e.4.2-hotfix-2 Bug D) ──────────────

  /**
   * Atomic hard delete. Replaces the Phase 6a.22a soft-delete + 5s undo
   * pattern, which left ghost slots behind when the user took any
   * other action during the window (Bug D2).
   *
   * Cleanups performed:
   *   • `contracts.splice(slotIndex, 1)`
   *   • `contractsDirty.splice(slotIndex, 1)` if present
   *   • `lastCalculateError`: drop key `slotIndex`, re-index higher keys
   *     down by 1 (the array shifted)
   *   • `contractDeletions`: scrubbed entirely (migration cleanup for
   *     engagements that still carry stale soft-delete entries from
   *     pre-hotfix-2 sessions)
   *   • `contractCount = contracts.length`
   *   • `transitionToStale` if status was `calculated` — any contract
   *     removal invalidates the cached result snapshot
   */
  async deleteContract(
    engagementId: string,
    contractIndex: number,
  ): Promise<EngagementHistoryEntry> {
    const engagement = await this.requireEngagement(engagementId);
    this.requireSlotInRange(engagement, contractIndex);

    const nextContracts = [...engagement.contracts];
    nextContracts.splice(contractIndex, 1);

    const nextDirty = engagement.contractsDirty
      ? [...engagement.contractsDirty]
      : undefined;
    nextDirty?.splice(contractIndex, 1);

    const nextErrors = reindexAfterRemoval(
      engagement.lastCalculateError,
      contractIndex,
    );

    // Phase 6e.4.2-hotfix-2 — drop the legacy `contractDeletions` field
    // at the object boundary so engagements that still carry stale
    // soft-delete state in localStorage are cleaned up on the next
    // delete. The field is no longer on the EngagementHistoryEntry type
    // but the runtime object may still have it from older sessions.
    const cleanedEngagement = engagement as unknown as Record<string, unknown>;
    if ("contractDeletions" in cleanedEngagement) {
      delete cleanedEngagement.contractDeletions;
    }

    const updated = transitionToStale({
      ...engagement,
      contracts: nextContracts,
      contractsDirty: nextDirty,
      contractCount: nextContracts.length,
      lastCalculateError: nextErrors,
      updatedAt: new Date().toISOString(),
    });
    await this.saveEngagement(updated);
    return updated;
  }

  private async requireEngagement(
    engagementId: string,
  ): Promise<EngagementHistoryEntry> {
    const engagement = await this.getEngagement(engagementId);
    if (!engagement) {
      throw new Error(
        `Engagement not found: ${engagementId}`,
      );
    }
    return engagement;
  }

  private requireSlotInRange(
    engagement: EngagementHistoryEntry,
    contractIndex: number,
  ): void {
    if (
      contractIndex < 0 ||
      contractIndex >= engagement.contracts.length
    ) {
      throw new Error(
        `Contract index out of range: ${contractIndex} (engagement has ${engagement.contracts.length} slots)`,
      );
    }
  }

  // ── Admin reads (SPEC-23) ───────────────────────────────────────────────
  //
  // Admin features are cloud-only by design. These stubs exist solely to
  // satisfy the `HistoryStorage` interface; production runs against
  // `SupabaseHistoryStorage` (wired in `container.ts`), so they are never
  // reached.

  async listEngagementsByOwner(): Promise<EngagementHistoryEntry[]> {
    throw new Error("admin features require cloud storage");
  }

  async listUserProfiles(): Promise<UserProfile[]> {
    throw new Error("admin features require cloud storage");
  }

  async setUserTier(): Promise<void> {
    throw new Error("admin features require cloud storage");
  }

  async getUsageStats(): Promise<UsageStats> {
    throw new Error("admin features require cloud storage");
  }

  async listComputeLog(): Promise<ComputeLogEntry[]> {
    throw new Error("admin features require cloud storage");
  }

  // ── Settings (SPEC-SETTINGS) — cloud-only by design ─────────────────────
  //
  // Profile + tier-request features are backed by the Supabase `profiles`
  // and `tier_change_requests` tables (with RLS + SECURITY DEFINER RPCs);
  // local mode has no such concepts. These stubs satisfy the interface so
  // typecheck passes; production runs against SupabaseHistoryStorage.

  async getMyProfile(): Promise<UserProfile> {
    throw new Error("settings features require cloud storage");
  }

  async updateMyFullName(): Promise<void> {
    throw new Error("settings features require cloud storage");
  }

  async requestTierChange(): Promise<void> {
    throw new Error("settings features require cloud storage");
  }

  async cancelTierChange(): Promise<void> {
    throw new Error("settings features require cloud storage");
  }

  async listMyTierRequests(): Promise<TierChangeRequest[]> {
    throw new Error("settings features require cloud storage");
  }

  async listPendingTierRequests(): Promise<PendingTierRequest[]> {
    throw new Error("admin features require cloud storage");
  }

  async reviewTierChange(): Promise<void> {
    throw new Error("admin features require cloud storage");
  }

  // ── Sharing (SPEC-SHARE) — not supported in local mode ────────────────
  async listMembers(): Promise<EngagementMember[]> {
    return [];
  }
  async addMemberByEmail(): Promise<void> {
    throw new Error("Sharing is not available in local storage mode.");
  }
  async updateMember(): Promise<void> {
    throw new Error("Sharing is not available in local storage mode.");
  }
  async removeMember(): Promise<void> {
    throw new Error("Sharing is not available in local storage mode.");
  }

  // SPEC-SHARE-B1 — local mode is always the owner.
  async getEngagementAccess(): Promise<EngagementAccessMeta | null> {
    return {
      isOwner: true,
      myRole: null,
      myCanInvite: false,
      ownerEmail: null,
      // Local mode has no admin concept (no profiles table).
      isAdmin: false,
    };
  }

  async purgeOldDeleted(): Promise<number> {
    const store = this.readEngagementStore();
    const cutoff = Date.now() - PURGE_AFTER_DAYS * 24 * 60 * 60 * 1000;
    const before = store.entries.length;
    store.entries = store.entries.filter((e) => {
      if (!e.isDeleted) return true;
      const deletedAt = e.deletedAt ? Date.parse(e.deletedAt) : Date.now();
      return deletedAt >= cutoff;
    });
    const purged = before - store.entries.length;
    if (purged > 0) this.writeEngagementStore(store);
    return purged;
  }

  // ── Engagement store internals ──────────────────────────────────────────

  /**
   * Reads the engagement store from localStorage; on first access, runs the
   * silent v1→v2 seed migration so users with existing single-contract
   * history see their old entries reflected as migrated engagements (with
   * `isStale: true`, requiring recalculation).
   */
  private readEngagementStore(): EngagementStore {
    const raw = localStorage.getItem(ENGAGEMENT_KEY);
    if (!raw) {
      // First-ever access — run migration if there's any v1 data to seed from.
      const migrated = this.migrateV1ToV2();
      this.writeEngagementStore(migrated);
      return migrated;
    }
    try {
      const parsed = JSON.parse(raw) as {
        version: number;
        entries?: EngagementHistoryEntry[];
        v1Migrated?: boolean;
      };

      // Phase 6a.18a — v2 → v3 migration. The on-disk shape is unchanged,
      // so the migration only normalises any entry that was written
      // before `isStale` became a required boolean (defensive — older
      // beta builds may have stored `undefined`).
      if (parsed.version === 2) {
        const migrated: EngagementStore = {
          version: ENGAGEMENT_SCHEMA_VERSION,
          entries: (parsed.entries ?? []).map((e) => migrateV2EntryToV3(e)),
          v1Migrated: parsed.v1Migrated ?? true,
        };
        this.writeEngagementStore(migrated);
        return migrated;
      }

      if (parsed.version !== ENGAGEMENT_SCHEMA_VERSION) {
        // Unknown future version — start clean rather than corrupt data.
        console.warn(
          "Unknown engagement schema version, resetting engagements store",
        );
        const fresh: EngagementStore = {
          version: ENGAGEMENT_SCHEMA_VERSION,
          entries: [],
          v1Migrated: true,
        };
        this.writeEngagementStore(fresh);
        return fresh;
      }
      const store: EngagementStore = {
        version: ENGAGEMENT_SCHEMA_VERSION,
        entries: parsed.entries ?? [],
        v1Migrated: parsed.v1Migrated ?? false,
      };
      // Already loaded once but never migrated (e.g., user installed the app
      // pre-engagement, then restored localStorage without the migration
      // flag). Seed silently.
      if (!store.v1Migrated) {
        const seeded = this.migrateV1ToV2(store.entries);
        this.writeEngagementStore(seeded);
        return seeded;
      }
      return store;
    } catch {
      const fresh: EngagementStore = {
        version: ENGAGEMENT_SCHEMA_VERSION,
        entries: [],
        v1Migrated: true,
      };
      this.writeEngagementStore(fresh);
      return fresh;
    }
  }

  /**
   * Persist the engagement store, with an emergency-reclaim retry on
   * quota exhaustion (BUG-1a / BUG-1b).
   *
   * On the first `QuotaExceededError` we hard-drop every soft-deleted
   * entry (TTL ignored — they are already deleted, the user wants them
   * gone) and retry the write ONCE. This turns the previously fatal
   * "storage full → calculate blocked" path into a self-healing one in
   * the common case (the quota was consumed by deleted-but-not-purged
   * snapshots). Only if the retry also fails do we fall back to the
   * silent-toast behaviour so the in-memory session is never blocked.
   *
   * `retrying` guards against unbounded recursion: the retry write
   * passes `retrying = true`, so a genuine over-quota live store (no
   * soft-deletes to reclaim) degrades to the toast exactly once.
   */
  private writeEngagementStore(
    store: EngagementStore,
    retrying = false,
  ): void {
    try {
      localStorage.setItem(ENGAGEMENT_KEY, JSON.stringify(store));
    } catch (err) {
      if (isQuotaExceeded(err)) {
        if (!retrying) {
          const before = store.entries.length;
          // Emergency reclaim: drop ALL soft-deleted entries regardless
          // of TTL. They are already invisible to the user; freeing
          // them is strictly better than blocking the active session.
          store.entries = store.entries.filter((e) => !e.isDeleted);
          if (store.entries.length < before) {
            this.writeEngagementStore(store, true);
            return;
          }
        }
        // Phase 6a.18a — silent failure, surface via toast. Session
        // continues with in-memory result so the auditor is not blocked.
        const message = i18n.t("engagement.storage.quotaExceeded");
        quotaExceededHandler(message);
        return;
      }
      throw err;
    }
  }

  /**
   * Silent v1→v2 seed: each legacy single-contract entry becomes a
   * "(Migrated Calculation)" engagement with one contract, no result, and
   * `isStale: true`. The user must recalculate to refresh.
   *
   * `console.log` only — no banner — per Ömer's directive.
   */
  private migrateV1ToV2(
    existingEntries: EngagementHistoryEntry[] = [],
  ): EngagementStore {
    const v1Raw = localStorage.getItem(STORAGE_KEY);
    let v1Entries: HistoryEntry[] = [];
    if (v1Raw) {
      try {
        const parsed = JSON.parse(v1Raw) as HistoryStore;
        if (parsed.version === SCHEMA_VERSION && parsed.entries) {
          v1Entries = parsed.entries;
        }
      } catch {
        // Corrupt v1 data — skip migration.
      }
    }

    const seeded: EngagementHistoryEntry[] = v1Entries.map((legacy) => {
      const auditEnd = addMonthsIso(
        legacy.input.commencementDate,
        legacy.input.leaseTermMonths,
      );
      const createdAt = legacy.savedAt ?? new Date().toISOString();
      return {
        id: `migrated-${legacy.id}`,
        clientName: "(Migrated Calculation)",
        auditPeriodStart: legacy.input.commencementDate,
        auditPeriodEnd: auditEnd,
        functionalCurrency:
          legacy.input.functionalCurrency ?? legacy.input.currency,
        // Phase 6c.2 — legacy v1 contracts migrate into an
        // `IFRS_FIRST_ADOPTION` engagement (matches the old `"AUTO"`
        // semantic). The auditor will be forced to re-choose if they
        // recalculate; the deprecation warning surfaces via the
        // backend's LEGACY_AUTO_MODE_DEPRECATED warning code.
        openingBalanceMode: "IFRS_FIRST_ADOPTION",
        contractCount: 1,
        contracts: [legacy.input],
        // No lastResult — user must recalculate via the engagement endpoint.
        isStale: true,
        isDeleted: false,
        createdAt,
        updatedAt: createdAt,
      };
    });

    if (seeded.length > 0) {
      console.log(
        `[history] Migrated ${seeded.length} v1 entries to v2 engagement schema`,
      );
    }

    return {
      version: ENGAGEMENT_SCHEMA_VERSION,
      entries: [...existingEntries, ...seeded],
      v1Migrated: true,
    };
  }
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Detect a QuotaExceededError across the various browser dispatch
 * paths. Firefox uses the DOMException name; older Safari paths surface
 * `code === 22`. A defensive `instanceof Error` guard keeps this
 * resilient to non-Error throws.
 */
function isQuotaExceeded(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const anyErr = err as { name?: string; code?: number };
  if (anyErr.name === "QuotaExceededError") return true;
  if (anyErr.name === "NS_ERROR_DOM_QUOTA_REACHED") return true;
  if (anyErr.code === 22) return true;
  return false;
}

/**
 * Phase 6a.18a — v2 → v3 entry migration. The on-disk shape is
 * unchanged, but pre-Phase-6a.18a betas may have written entries
 * without `isStale` set (it became required between Phase 6a.10 and
 * Phase 6a.18a). Backfill to `false` defensively — older callers that
 * lacked a `lastResult` should be treated as fresh draft engagements.
 */
function migrateV2EntryToV3(
  entry: EngagementHistoryEntry,
): EngagementHistoryEntry {
  return {
    ...entry,
    isStale: entry.isStale ?? false,
  };
}

/**
 * Phase 6a.22a — re-key a sparse index→value map after a slot is
 * spliced out of the parent array. Entries at indices <`removedIndex`
 * keep their key; entries at indices >`removedIndex` shift down by 1
 * to match the post-splice slot numbering. The entry AT `removedIndex`
 * (if any) is dropped. Returns `undefined` when the result would be
 * empty, so callers can keep the optional field absent from the
 * persisted entry.
 */
export function reindexAfterRemoval<V>(
  map: Record<number, V> | undefined,
  removedIndex: number,
): Record<number, V> | undefined {
  if (!map) return undefined;
  const next: Record<number, V> = {};
  for (const [keyStr, value] of Object.entries(map)) {
    const key = Number(keyStr);
    if (key === removedIndex) continue;
    next[key < removedIndex ? key : key - 1] = value;
  }
  return Object.keys(next).length > 0 ? next : undefined;
}

/**
 * Add `months` to an ISO date string. Pure UTC arithmetic to avoid TZ
 * quirks. `2025-01-31 + 1 month = 2025-02-28` (Date semantics). For our
 * use case (audit period seeding from migration) the exact day-end is
 * unimportant; the user will recalculate anyway.
 */
function addMonthsIso(iso: string, months: number): string {
  const [yStr, mStr, dStr] = iso.split("-");
  const y = Number(yStr);
  const m = Number(mStr) - 1;
  const d = Number(dStr);
  const dt = new Date(Date.UTC(y, m + months, d));
  const yyyy = dt.getUTCFullYear().toString().padStart(4, "0");
  const mm = (dt.getUTCMonth() + 1).toString().padStart(2, "0");
  const dd = dt.getUTCDate().toString().padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
